<template>
  <FreeAlert
    ref="alertRef"
    :duration="alertDuration"
    :persistent="persistent"
    v-bind="$attrs"
  >
    <template #icon="{ content }">
      <slot name="icon" v-bind="{ content, type: content?.type }">
        <component :is="IconComponent" v-if="IconComponent" />
      </slot>
    </template>
    <template #default="{ content }">
      <slot v-bind="{ content }">
        {{ content?.message || content }}
      </slot>
    </template>
  </FreeAlert>
</template>

<script setup lang="ts">
  import { ref, watch, computed, onBeforeUnmount } from 'vue';
  import type { ComponentPublicInstance, Component } from 'vue';

  import FreeAlert from './FreeAlert.vue';

  interface UseAlertResult {
    currentAlert: {
      value: {
        isVisible: boolean;
        message?: string;
        type?: string;
        duration?: number;
        [key: string]: unknown;
      } | null;
    };
    show: (props: {
      type?: string;
      duration?: number;
      message: string;
    }) => void;
    hide: (id?: string) => void;
  }

  const props = withDefaults(
    defineProps<{
      useAlert: (namespace?: string) => UseAlertResult;
      duration?: number;
      persistent?: boolean;
      icons?: Record<string, Component>;
    }>(),
    {
      duration: 5000,
      persistent: false,
      icons: () => ({}),
    },
  );

  const alertRef = ref<ComponentPublicInstance<
    InstanceType<typeof FreeAlert>
  > | null>(null);

  const { currentAlert } = props.useAlert();

  // Compute duration from current alert or use prop default
  const alertDuration = computed(() => {
    return currentAlert.value?.duration ?? props.duration;
  });

  // Compute icon component based on currentAlert type
  const IconComponent = computed(() => {
    const type = currentAlert.value?.type;
    if (type && props.icons && props.icons[type]) {
      return props.icons[type];
    }
    // Fallback to default icon if provided
    if (props.icons && props.icons.default) {
      return props.icons.default;
    }
    return null;
  });

  // Watch for changes in currentAlert and control FreeAlert accordingly
  watch(
    () => currentAlert.value,
    alert => {
      if (!alertRef.value) return;

      if (alert?.isVisible) {
        // Alert is visible, show it with the alert content
        alertRef.value.show(alert);
      } else {
        // Alert is not visible, hide it
        alertRef.value.hide();
      }
    },
    { immediate: true },
  );

  onBeforeUnmount(() => {
    if (alertRef.value) {
      alertRef.value.hide();
    }
  });
</script>
